import axios, { AxiosResponse } from 'axios';

import {
  AdminWorkspaceContextData,
  AdminWorkspaceSessionData,
  CompanyAccessCodeStateData,
  CompanyAccessCodeUpdateData,
  CompanyApplication,
  CompanyApplicationStatus,
  CompanyMembershipDecision,
  CompanyMembershipDecisionData,
  CompanyPendingApplication,
  hasAdminSurfaceIdentity,
  parseCompanyApplications,
  parseCompanyAccessCodeState,
  parseCompanyAccessCodeUpdate,
  parseCompanyMembershipDecision,
  parseAdminWorkspaceContext,
  parseMultiCompanySuccessEnvelope,
  parsePlatformCompanyChangeHistory,
  parsePlatformCompanyInventory,
  PlatformCompanyChangeHistoryData,
  PlatformCompanyInventoryData,
  PlatformCompanyInventoryItem
} from 'src/contracts/multiCompany/v1';
import {
  PlatformWorkforceData,
  PlatformWorkforceRequirement,
  PlatformWorkforceRequirementCompletion,
  PlatformWorkforceStatusFilter,
  PlatformWorkerDetailData
} from 'src/contracts/platformWorkforce';
import {
  PlatformPositionCatalogData,
  PlatformPositionInput
} from 'src/contracts/platformPositionCatalog';
import {
  PlatformOnboardingConfiguration,
  PlatformOnboardingData,
  PlatformOnboardingInput
} from 'src/contracts/platformOnboarding';
import {
  PlatformBulkUpdateRequestInput,
  PlatformBulkUpdateRequestRecord
} from 'src/contracts/platformUpdateRequests';
import {
  ADMIN_WORKSPACE_CONTEXT,
  PLATFORM_COMPANIES
} from 'src/helper/url_helpter';
import {
  companyTenantApiPath,
  platformTenantPath
} from 'src/workspaces/tenantWorkspace';

export async function getAdminWorkspaceContext(): Promise<AdminWorkspaceSessionData> {
  const response: AxiosResponse<unknown> = await axios.get(
    ADMIN_WORKSPACE_CONTEXT
  );
  const envelope = parseMultiCompanySuccessEnvelope<unknown>(response.data);
  if (
    !envelope ||
    !hasAdminSurfaceIdentity(envelope.capabilities) ||
    (!envelope.capabilities.admin_surface_identity_realms &&
      (!envelope.capabilities.company_explicit_tenant_routes ||
        !envelope.capabilities.admin_explicit_tenant_context))
  ) {
    throw new Error('Admin workspace capability is unavailable');
  }
  const workspace = parseAdminWorkspaceContext(envelope.data);
  if (!workspace) {
    throw new Error('Admin workspace response is invalid');
  }
  return { ...workspace, capabilities: envelope.capabilities };
}

export async function getPlatformBulkUpdateRequests(): Promise<
  PlatformBulkUpdateRequestRecord[]
> {
  const response: AxiosResponse<unknown> = await axios.get(
    '/platform/update-requests'
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  if (!envelope || !Array.isArray(envelope.data)) {
    throw new Error('Platform update request response is invalid');
  }
  return envelope.data as PlatformBulkUpdateRequestRecord[];
}

export async function createPlatformBulkUpdateRequest(
  input: PlatformBulkUpdateRequestInput
): Promise<PlatformBulkUpdateRequestRecord> {
  const response: AxiosResponse<unknown> = await axios.post(
    '/platform/update-requests',
    input
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  const record = envelope?.data;
  if (
    !record ||
    typeof record.requestId !== 'string' ||
    !Array.isArray(record.targets)
  ) {
    throw new Error('Platform update request response is invalid');
  }
  return record as PlatformBulkUpdateRequestRecord;
}

export interface PlatformCompanyInventoryQuery {
  limit?: number;
  afterCompanyId?: number;
  search?: string;
  status?:
    | 'active'
    | 'provisioning'
    | 'suspended'
    | 'closed'
    | 'inactive'
    | 'legacy';
}

export type PlatformCompanyLifecycleAction =
  | 'SUSPEND'
  | 'CLOSE'
  | 'REACTIVATE';

export interface PlatformCompanyLifecycleResult {
  companyId: number;
  companyName: string;
  previousStatus: string;
  lifecycleStatus: string;
  changedAt: string;
  auditId: number;
}

export async function updatePlatformCompanyLifecycle(
  companyId: number,
  input: {
    action: PlatformCompanyLifecycleAction;
    expectedStatus: string;
    reason: string;
    confirmationName: string;
  }
): Promise<PlatformCompanyLifecycleResult> {
  if (!Number.isSafeInteger(companyId) || companyId <= 0) {
    throw new Error('Platform company ID is invalid');
  }
  const response: AxiosResponse<unknown> = await axios.put(
    `${PLATFORM_COMPANIES}/${companyId}/lifecycle`,
    input
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  const result = envelope?.data;
  if (
    !result ||
    result.companyId !== companyId ||
    typeof result.companyName !== 'string' ||
    typeof result.previousStatus !== 'string' ||
    typeof result.lifecycleStatus !== 'string' ||
    typeof result.changedAt !== 'string' ||
    !Number.isSafeInteger(result.auditId)
  ) {
    throw new Error('Platform company lifecycle response is invalid');
  }
  return result as PlatformCompanyLifecycleResult;
}

export interface PlatformWorkforceQuery {
  limit?: number;
  afterUserId?: number;
  search?: string;
  companyId?: number;
  requirement?: PlatformWorkforceRequirement;
  completion?: PlatformWorkforceRequirementCompletion;
  status?: PlatformWorkforceStatusFilter;
}

export async function getPlatformWorkforce(
  query: PlatformWorkforceQuery = {}
): Promise<PlatformWorkforceData> {
  const limit = optionalPositiveInteger(
    query.limit,
    100,
    'Platform workforce query is invalid'
  );
  const afterUserId = optionalPositiveInteger(
    query.afterUserId,
    undefined,
    'Platform workforce query is invalid'
  );
  const search = query.search?.trim();
  const companyId = optionalPositiveInteger(
    query.companyId,
    undefined,
    'Platform workforce query is invalid'
  );
  const requirement = query.requirement;
  const completion = query.completion;
  const status = query.status;
  if (search && search.length > 100) {
    throw new Error('Platform workforce query is invalid');
  }
  if (requirement && companyId === undefined) {
    throw new Error('Platform workforce requirement needs a company');
  }
  if (completion && !requirement) {
    throw new Error('Platform workforce completion needs a requirement');
  }
  const response: AxiosResponse<unknown> = await axios.get(
    '/platform/workforce',
    {
      params: {
        ...(limit === undefined ? {} : { limit }),
        ...(afterUserId === undefined ? {} : { afterUserId }),
        ...(search ? { search } : {}),
        ...(companyId === undefined ? {} : { companyId }),
        ...(requirement === undefined ? {} : { requirement }),
        ...(completion === undefined ? {} : { completion }),
        ...(status === undefined ? {} : { status })
      }
    }
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  const data = envelope?.data;
  if (
    !data ||
    data.role !== 'SUPER_ADMIN' ||
    !Number.isSafeInteger(data.actorId) ||
    !Array.isArray(data.items) ||
    !data.summary ||
    !('requirementFilter' in data)
  ) {
    throw new Error('Platform workforce response is invalid');
  }
  return data as PlatformWorkforceData;
}

const optionalPositiveInteger = (
  value: number | undefined,
  maximum?: number,
  errorMessage = 'Platform company inventory query is invalid'
): number | undefined => {
  if (value === undefined) return undefined;
  if (
    !Number.isSafeInteger(value) ||
    value <= 0 ||
    (maximum !== undefined && value > maximum)
  ) {
    throw new Error(errorMessage);
  }
  return value;
};

export async function getPlatformCompanyInventory(
  query: PlatformCompanyInventoryQuery = {}
): Promise<PlatformCompanyInventoryData> {
  const limit = optionalPositiveInteger(query.limit, 100);
  const afterCompanyId = optionalPositiveInteger(query.afterCompanyId);
  const search = query.search?.trim();
  if (search && search.length > 100) {
    throw new Error('Platform company inventory query is invalid');
  }
  const response: AxiosResponse<unknown> = await axios.get(PLATFORM_COMPANIES, {
    params: {
      ...(limit === undefined ? {} : { limit }),
      ...(afterCompanyId === undefined ? {} : { afterCompanyId }),
      ...(search ? { search } : {}),
      ...(query.status ? { status: query.status } : {})
    }
  });
  const envelope = parseMultiCompanySuccessEnvelope<unknown>(response.data);
  if (
    !envelope ||
    !hasAdminSurfaceIdentity(envelope.capabilities) ||
    (!envelope.capabilities.admin_surface_identity_realms &&
      !envelope.capabilities.admin_explicit_tenant_context)
  ) {
    throw new Error('Platform company inventory capability is unavailable');
  }
  const inventory = parsePlatformCompanyInventory(envelope.data);
  if (!inventory) {
    throw new Error('Platform company inventory response is invalid');
  }
  return inventory;
}

export interface PlatformCompanyLookup {
  actorId: number;
  item: PlatformCompanyInventoryItem;
}

export async function getPlatformCompanyById(
  companyId: number
): Promise<PlatformCompanyLookup | null> {
  if (!Number.isSafeInteger(companyId) || companyId <= 0) {
    throw new Error('Platform company ID is invalid');
  }

  try {
    const response: AxiosResponse<unknown> = await axios.get(
      `${PLATFORM_COMPANIES}/${companyId}`
    );
    const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
    const lookup = envelope?.data;
    if (
      !lookup ||
      lookup.role !== 'SUPER_ADMIN' ||
      !Number.isSafeInteger(lookup.actorId)
    ) {
      throw new Error('Platform company response is invalid');
    }
    const inventory = parsePlatformCompanyInventory({
      actorId: lookup.actorId,
      role: lookup.role,
      items: [lookup.item],
      totalCount: 1,
      nextAfterCompanyId: null
    });
    if (!inventory || inventory.items[0]?.company.id !== companyId) {
      throw new Error('Platform company response is invalid');
    }
    return { actorId: inventory.actorId, item: inventory.items[0] };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404)
      return null;
    throw error;
  }
}

export async function getPlatformWorker(
  workerId: number
): Promise<PlatformWorkerDetailData> {
  const id = optionalPositiveInteger(
    workerId,
    undefined,
    'Platform worker ID is invalid'
  );
  if (id === undefined) throw new Error('Platform worker ID is invalid');
  const response: AxiosResponse<unknown> = await axios.get(
    `/platform/workforce/${id}`
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  const data = envelope?.data;
  if (
    !data ||
    data.role !== 'SUPER_ADMIN' ||
    !Number.isSafeInteger(data.actorId) ||
    !data.worker ||
    data.worker.id !== id
  ) {
    throw new Error('Platform worker response is invalid');
  }
  return data as PlatformWorkerDetailData;
}

export async function getPlatformPositionCatalog(
  query: {
    search?: string;
    categoryId?: number;
  } = {}
): Promise<PlatformPositionCatalogData> {
  const categoryId = optionalPositiveInteger(
    query.categoryId,
    undefined,
    'Platform position catalog query is invalid'
  );
  const search = query.search?.trim();
  if (search && search.length > 100) {
    throw new Error('Platform position catalog query is invalid');
  }
  const response: AxiosResponse<unknown> = await axios.get(
    '/platform/positions',
    {
      params: {
        ...(search ? { search } : {}),
        ...(categoryId === undefined ? {} : { categoryId })
      }
    }
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  const data = envelope?.data;
  if (
    !data ||
    data.role !== 'SUPER_ADMIN' ||
    !Number.isSafeInteger(data.actorId) ||
    !Array.isArray(data.items) ||
    !Array.isArray(data.categories) ||
    !Number.isSafeInteger(data.totalCount)
  ) {
    throw new Error('Platform position catalog response is invalid');
  }
  return data as PlatformPositionCatalogData;
}

export async function getPlatformOnboardingConfiguration(): Promise<PlatformOnboardingData> {
  const response: AxiosResponse<unknown> = await axios.get(
    '/platform/onboarding-configuration'
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  const data = envelope?.data;
  if (
    !data ||
    data.role !== 'SUPER_ADMIN' ||
    !Number.isSafeInteger(data.actorId) ||
    !data.configuration ||
    !Number.isSafeInteger(data.configuration.version) ||
    !Array.isArray(data.revisions)
  ) {
    throw new Error('Platform Onboarding response is invalid');
  }
  return data as PlatformOnboardingData;
}

export async function updatePlatformOnboardingConfiguration(
  input: PlatformOnboardingInput
): Promise<PlatformOnboardingConfiguration> {
  const response: AxiosResponse<unknown> = await axios.put(
    '/platform/onboarding-configuration',
    input
  );
  const envelope = parseMultiCompanySuccessEnvelope<any>(response.data);
  const data = envelope?.data;
  if (
    !data ||
    data.role !== 'SUPER_ADMIN' ||
    !Number.isSafeInteger(data.actorId) ||
    !data.configuration ||
    !Number.isSafeInteger(data.configuration.version)
  ) {
    throw new Error('Platform Onboarding update response is invalid');
  }
  return data.configuration as PlatformOnboardingConfiguration;
}

export async function createPlatformPosition(input: PlatformPositionInput) {
  const response = await axios.post('/platform/positions', input);
  return parseMultiCompanySuccessEnvelope<any>(response.data)?.data?.position;
}

export async function updatePlatformPosition(
  positionId: number,
  input: Partial<PlatformPositionInput>
) {
  const response = await axios.put(`/platform/positions/${positionId}`, input);
  return parseMultiCompanySuccessEnvelope<any>(response.data)?.data?.position;
}

export async function removePlatformPosition(positionId: number) {
  await axios.delete(`/platform/positions/${positionId}`);
}

export async function createPlatformPositionCategory(name: string) {
  await axios.post('/platform/positions/categories', { name });
}

export async function updatePlatformPositionCategory(
  categoryId: number,
  name: string
) {
  await axios.put(`/platform/positions/categories/${categoryId}`, { name });
}

export async function removePlatformPositionCategory(categoryId: number) {
  await axios.delete(`/platform/positions/categories/${categoryId}`);
}

export interface PlatformCompanyChangeHistoryQuery {
  limit?: number;
  beforeAuditId?: number;
}

export async function getPlatformCompanyChangeHistory(
  companyId: number,
  query: PlatformCompanyChangeHistoryQuery = {}
): Promise<PlatformCompanyChangeHistoryData> {
  const queryError = 'Platform company change history query is invalid';
  const limit = optionalPositiveInteger(query.limit, 100, queryError);
  const beforeAuditId = optionalPositiveInteger(
    query.beforeAuditId,
    undefined,
    queryError
  );
  const path = platformTenantPath(companyId, 'change-history');
  const response: AxiosResponse<unknown> = await axios.get(path, {
    params: {
      ...(limit === undefined ? {} : { limit }),
      ...(beforeAuditId === undefined ? {} : { beforeAuditId })
    }
  });
  const envelope = parseMultiCompanySuccessEnvelope<unknown>(response.data);
  if (
    !envelope ||
    !hasAdminSurfaceIdentity(envelope.capabilities) ||
    (!envelope.capabilities.admin_surface_identity_realms &&
      !envelope.capabilities.admin_explicit_tenant_context)
  ) {
    throw new Error(
      'Platform company change history capability is unavailable'
    );
  }
  const history = parsePlatformCompanyChangeHistory(envelope.data, companyId);
  if (!history) {
    throw new Error('Platform company change history response is invalid');
  }
  return history;
}

export async function getPlatformCompanyDomainActivity(
  companyId: number,
  query: PlatformCompanyChangeHistoryQuery = {}
): Promise<PlatformCompanyChangeHistoryData> {
  const queryError = 'Platform company domain activity query is invalid';
  const limit = optionalPositiveInteger(query.limit, 100, queryError);
  const beforeAuditId = optionalPositiveInteger(
    query.beforeAuditId,
    undefined,
    queryError
  );
  const path = `${platformTenantPath(companyId, 'change-history')}/domain`;
  const response: AxiosResponse<unknown> = await axios.get(path, {
    params: {
      ...(limit === undefined ? {} : { limit }),
      ...(beforeAuditId === undefined ? {} : { beforeAuditId })
    }
  });
  const envelope = parseMultiCompanySuccessEnvelope<unknown>(response.data);
  const history = parsePlatformCompanyChangeHistory(envelope?.data, companyId);
  if (!history) {
    throw new Error('Platform company domain activity response is invalid');
  }
  return history;
}

export async function getCompanyPendingApplications(
  workspace: AdminWorkspaceContextData,
  routeCompanyId: unknown
): Promise<CompanyPendingApplication[]> {
  return getCompanyApplications(workspace, routeCompanyId, 'pending');
}

export async function getCompanyApplications(
  workspace: AdminWorkspaceContextData,
  routeCompanyId: unknown,
  bucket: CompanyApplicationStatus
): Promise<CompanyApplication[]> {
  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    'memberships/applications'
  );
  const response: AxiosResponse<unknown> = await axios.get(path, {
    params: { bucket }
  });
  const envelope = parseMultiCompanySuccessEnvelope<unknown>(response.data);
  if (
    !envelope ||
    !envelope.capabilities.surface_identity_realms ||
    !envelope.capabilities.company_explicit_tenant_routes ||
    !envelope.capabilities.multi_company_membership_read ||
    !envelope.capabilities.admin_explicit_tenant_context
  ) {
    throw new Error('Company applications capability is unavailable');
  }
  const applications = parseCompanyApplications(
    envelope.data,
    workspace.company.id,
    bucket
  );
  if (!applications) {
    throw new Error('Company applications response is invalid');
  }
  return applications;
}

export interface CompanyMembershipDecisionInput {
  expectedStatus: CompanyApplicationStatus;
  decision: CompanyMembershipDecision;
  reason?: string;
}

export async function decideCompanyApplication(
  workspace: AdminWorkspaceContextData,
  routeCompanyId: unknown,
  membershipId: number,
  input: CompanyMembershipDecisionInput
): Promise<CompanyMembershipDecisionData> {
  if (!Number.isSafeInteger(membershipId) || membershipId <= 0) {
    throw new Error('Company membership decision is invalid');
  }
  const reason = input.reason?.trim();
  const validReason =
    reason !== undefined && reason.length >= 3 && reason.length <= 500;
  const validDecision =
    (input.decision === 'approve' && !reason) ||
    (input.expectedStatus === 'pending' &&
      input.decision === 'reject' &&
      validReason) ||
    (input.expectedStatus === 'rejected' &&
      input.decision === 'cancel' &&
      validReason);
  if (!validDecision) {
    throw new Error('Company membership decision is invalid');
  }

  const path = companyTenantApiPath(
    workspace,
    routeCompanyId,
    `memberships/${membershipId}/decision`
  );
  const response: AxiosResponse<unknown> = await axios.post(path, {
    expectedStatus: input.expectedStatus,
    decision: input.decision,
    ...(reason ? { reason } : {})
  });
  const envelope = parseMultiCompanySuccessEnvelope<unknown>(response.data);
  if (
    !envelope ||
    !envelope.capabilities.multi_company_membership_dual_write ||
    !envelope.capabilities.multi_company_membership_read ||
    !envelope.capabilities.surface_identity_realms ||
    !envelope.capabilities.company_explicit_tenant_routes ||
    !envelope.capabilities.admin_explicit_tenant_context
  ) {
    throw new Error('Company membership decisions capability is unavailable');
  }
  const result = parseCompanyMembershipDecision(
    envelope.data,
    workspace.company.id,
    membershipId
  );
  if (!result) {
    throw new Error('Company membership decision response is invalid');
  }
  return result;
}

export async function saveCompanyAccessCode(
  workspace: AdminWorkspaceContextData,
  routeCompanyId: unknown,
  code: string
): Promise<CompanyAccessCodeUpdateData> {
  const path = companyTenantApiPath(workspace, routeCompanyId, 'access-code');
  const response: AxiosResponse<unknown> = await axios.put(path, { code });
  const envelope = parseMultiCompanySuccessEnvelope<unknown>(response.data);
  if (
    !envelope ||
    !envelope.capabilities.surface_identity_realms ||
    !envelope.capabilities.company_explicit_tenant_routes ||
    !envelope.capabilities.admin_explicit_tenant_context ||
    !envelope.capabilities.company_access_code_rotation
  ) {
    throw new Error('Company access code management capability is unavailable');
  }
  const update = parseCompanyAccessCodeUpdate(
    envelope.data,
    workspace.company.id
  );
  if (!update) {
    throw new Error('Company access code management response is invalid');
  }
  return update;
}

export async function getCompanyAccessCode(
  workspace: AdminWorkspaceContextData,
  routeCompanyId: unknown
): Promise<CompanyAccessCodeStateData> {
  const path = companyTenantApiPath(workspace, routeCompanyId, 'access-code');
  const response: AxiosResponse<unknown> = await axios.get(path);
  const envelope = parseMultiCompanySuccessEnvelope<unknown>(response.data);
  if (
    !envelope ||
    !envelope.capabilities.surface_identity_realms ||
    !envelope.capabilities.company_explicit_tenant_routes ||
    !envelope.capabilities.admin_explicit_tenant_context ||
    !envelope.capabilities.company_access_code_rotation
  ) {
    throw new Error('Company access code management capability is unavailable');
  }
  const state = parseCompanyAccessCodeState(
    envelope.data,
    workspace.company.id
  );
  if (!state) {
    throw new Error('Company access code management response is invalid');
  }
  return state;
}
